import { Link, NavLink, Outlet } from "react-router-dom";
import { useState } from "react";
import { Menu, X, ChevronRight } from "lucide-react";
import { Button } from "./ui/button";
import { useAuth } from "../context/AuthContext";

const NAV = [
  { to: "/", label: "Home", end: true },
  { to: "/for-businesses", label: "For Businesses" },
  { to: "/for-gyms", label: "For Gyms" },
  { to: "/pricing", label: "Pricing" },
  { to: "/affiliates", label: "Affiliates" },
];

export default function Layout() {
  const [open, setOpen] = useState(false);
  const { user } = useAuth();

  return (
    <div className="min-h-screen flex flex-col bg-[#0A0E17]" data-testid="app-layout">
      {/* HEADER */}
      <header className="sticky top-0 z-50 glass-dark border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 group">
            <span className="font-display font-semibold tracking-[0.25em] text-xl text-white">FITEST</span>
          </Link>

          <nav className="hidden lg:flex items-center gap-10">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                className={({ isActive }) =>
                  `text-[11px] uppercase tracking-[0.2em] font-semibold transition-all duration-300 ${
                    isActive ? "text-brand" : "text-slate-400 hover:text-white"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-6">
            {user ? (
              <Link to="/dashboard">
                <Button className="btn-glow rounded-full px-6 h-10 text-xs uppercase tracking-widest font-bold">Dashboard</Button>
              </Link>
            ) : (
              <>
                <Link to="/login" className="text-[11px] font-semibold uppercase tracking-widest text-slate-400 hover:text-white transition-colors">
                  Login
                </Link>
                <Link to="/request-demo">
                  <Button className="btn-glow rounded-full px-6 h-10 text-xs uppercase tracking-widest font-bold">
                    Request Demo
                  </Button>
                </Link>
              </>
            )}
          </div>

          <button onClick={() => setOpen((v) => !v)} className="lg:hidden text-white">
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </header>

      {/* MOBILE MENU */}
      {open && (
        <div className="lg:hidden bg-[#0D1B2A] border-b border-white/10 p-8 space-y-4">
          {NAV.map((n) => (
            <Link key={n.to} to={n.to} onClick={() => setOpen(false)} className="block text-slate-300 py-2 uppercase tracking-widest text-xs">
              {n.label}
            </Link>
          ))}
        </div>
      )}

      {/* MAIN CONTENT */}
      <main className="flex-1">
        <Outlet />
      </main>

      {/* FOOTER */}
      <footer className="border-t border-white/5 mt-32 bg-[#060D1A]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid grid-cols-2 md:grid-cols-4 gap-12 border-b border-white/5">
          <div>
            <span className="font-display font-semibold tracking-[0.2em] text-white">FITEST</span>
            <p className="mt-6 text-sm text-slate-400 leading-relaxed">
              Diagnostic health auditing for elite gyms and HR teams.
            </p>
          </div>
          <div>
            <h4 className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-6 font-bold">Platform</h4>
            <ul className="space-y-4 text-sm text-slate-400">
              <li><Link to="/for-businesses">For Businesses</Link></li>
              <li><Link to="/for-gyms">For Gyms</Link></li>
              <li><Link to="/pricing">Pricing</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-6 font-bold">Legal</h4>
            <ul className="space-y-4 text-sm text-slate-400">
              <li><Link to="/legal/privacy">Privacy Policy</Link></li>
              <li><Link to="/legal/terms">Terms of Service</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-6 font-bold">Support</h4>
            <ul className="space-y-4 text-sm text-slate-400">
              <li><Link to="/faq">FAQ</Link></li>
              <li><Link to="/contact">Contact</Link></li>
              <li className="text-brand pt-2">hello@fitest.co.uk</li>
            </ul>
          </div>
        </div>

        {/* CORPORATE INFO BAR */}
        <div className="py-8 bg-[#040812]">
          <div className="max-w-7xl mx-auto px-6 text-center">
            <p className="text-[11px] text-slate-600 tracking-wide">
              Fitest is a trading name of Leadsopedia Limited (Company: 13145058) &bull; Registered Office: 5 Brayford Square, London, E1 0SG &bull; &copy; {new Date().getFullYear()} Fitest. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
